import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Plus, Save, Trash2, Loader2 } from "lucide-react";

interface Row {
  id: string;
  conference_slug: string;
  section: "theme" | "speakers" | "venue" | "food" | "program" | "worship" | "flyer";
  region_id: string | null;
  title: string | null;
  subtitle: string | null;
  body: string | null;
  data: any;
  order_index: number;
  is_active: boolean;
}
interface Region { id: string; name: string }

const SLUG = "regional-conference-2026";

export const ConferenceContentManager = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<Row[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const [{ data: c }, { data: r }] = await Promise.all([
      (supabase.from("conference_content" as any) as any)
        .select("*")
        .eq("conference_slug", SLUG)
        .order("section")
        .order("order_index"),
      supabase.from("regions").select("id,name").order("name"),
    ]);
    setRows((c as Row[]) || []);
    setRegions((r as Region[]) || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const update = (id: string, patch: Partial<Row>) =>
    setRows((p) => p.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const save = async (row: Row) => {
    setSavingId(row.id);
    const { error } = await (supabase.from("conference_content" as any) as any)
      .update({
        title: row.title,
        subtitle: row.subtitle,
        body: row.body,
        data: row.data,
        region_id: row.region_id,
        order_index: row.order_index,
        is_active: row.is_active,
      })
      .eq("id", row.id);
    setSavingId(null);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Saved" });
    }
  };

  const remove = async (row: Row) => {
    if (!confirm(`Delete this ${row.section} entry?`)) return;
    const { error } = await (supabase.from("conference_content" as any) as any).delete().eq("id", row.id);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted" });
      load();
    }
  };

  const add = async (section: Row["section"]) => {
    const base: any = {
      conference_slug: SLUG,
      section,
      order_index: rows.filter((r) => r.section === section).length + 1,
      is_active: true,
      title:
        section === "theme" ? "New Theme" :
        section === "speakers" ? "New Speaker" :
        section === "venue" ? "New Venue" :
        section === "program" ? "Day 1 — Date" :
        section === "worship" ? "Worship Session" :
        section === "flyer" ? "Region Flyer" :
        "Day 1 — Date",
      subtitle: "",
      body: "",
      data:
        section === "food" ? { items: [] } :
        section === "program" ? { items: [] } :
        section === "theme" ? { sub_themes: [] } :
        section === "venue" ? { address_line1: "", address_line2: "", check_in: "", parking: "", map_url: "" } :
        section === "flyer" ? { image_url: "" } :
        {},
    };
    const { error } = await (supabase.from("conference_content" as any) as any).insert(base);
    if (error) toast({ title: "Add failed", description: error.message, variant: "destructive" });
    else load();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const bySection = (s: Row["section"]) => rows.filter((r) => r.section === s);
  const contentTab = searchParams.get("contentTab") || "theme";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conference Content (Annual Regional Conference 2026)</CardTitle>
        <p className="text-sm text-muted-foreground">
          Edit Theme, Speakers, Worship (common), and per-region Flyer, Venue, Food Menu (per day), Program Schedule, and Worship details. The Flyer tab is the manual flyer generator for publishing finished regional flyers. Changes save per row.
        </p>
      </CardHeader>
      <CardContent>
        <Tabs value={contentTab} onValueChange={(value) => {
          const next = new URLSearchParams(searchParams);
          next.set("contentTab", value);
          setSearchParams(next, { replace: true });
        }}>
          <TabsList className="grid grid-cols-4 md:grid-cols-7 mb-6 h-auto">
            <TabsTrigger value="theme">Theme</TabsTrigger>
            <TabsTrigger value="speakers">Speakers</TabsTrigger>
            <TabsTrigger value="flyer">Manual Flyer Generator</TabsTrigger>
            <TabsTrigger value="venue">Venue</TabsTrigger>
            <TabsTrigger value="food">Food</TabsTrigger>
            <TabsTrigger value="program">Program</TabsTrigger>
            <TabsTrigger value="worship">Worship</TabsTrigger>
          </TabsList>

          {(["theme", "speakers", "flyer", "venue", "food", "program", "worship"] as const).map((section) => (
            <TabsContent key={section} value={section} className="space-y-4">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => add(section)}>
                  <Plus className="w-4 h-4 mr-1" /> Add {section === "flyer" ? "manual flyer" : section}
                </Button>
              </div>

              {bySection(section).length === 0 && (
                <p className="text-sm text-muted-foreground italic">No entries yet.</p>
              )}

              {bySection(section).map((row) => (
                <div key={row.id} className="border rounded-lg p-4 space-y-3 bg-card">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Title</Label>
                      <Input value={row.title || ""} onChange={(e) => update(row.id, { title: e.target.value })} />
                    </div>
                    <div>
                      <Label>
                        {section === "venue" ? "Dates / Tag"
                          : section === "food" ? "Day label (e.g. Friday Jul 24)"
                          : section === "program" ? "Day label / Date"
                          : section === "flyer" ? "Tagline"
                          : "Subtitle / Role"}
                      </Label>
                      <Input value={row.subtitle || ""} onChange={(e) => update(row.id, { subtitle: e.target.value })} />
                    </div>
                  </div>

                  {(section === "venue" || section === "food" || section === "program" || section === "flyer" || section === "worship") && (
                    <div>
                      <Label>Region {section === "worship" ? "(leave empty for common worship)" : ""}</Label>
                      <Select
                        value={row.region_id || "__none__"}
                        onValueChange={(v) => update(row.id, { region_id: v === "__none__" ? null : v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select region" /></SelectTrigger>
                        <SelectContent>
                          {section === "worship" && <SelectItem value="__none__">— Common (all regions) —</SelectItem>}
                          {regions.map((r) => (
                            <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label>{section === "speakers" ? "Bio" : "Body / Description"}</Label>
                    <Textarea
                      rows={3}
                      value={row.body || ""}
                      onChange={(e) => update(row.id, { body: e.target.value })}
                    />
                  </div>

                  {section === "venue" && (
                    <div className="grid md:grid-cols-2 gap-3">
                      <Input placeholder="Address line 1" value={row.data?.address_line1 || ""}
                        onChange={(e) => update(row.id, { data: { ...row.data, address_line1: e.target.value } })} />
                      <Input placeholder="Address line 2" value={row.data?.address_line2 || ""}
                        onChange={(e) => update(row.id, { data: { ...row.data, address_line2: e.target.value } })} />
                      <Input placeholder="Check-in info" value={row.data?.check_in || ""}
                        onChange={(e) => update(row.id, { data: { ...row.data, check_in: e.target.value } })} />
                      <Input placeholder="Parking info" value={row.data?.parking || ""}
                        onChange={(e) => update(row.id, { data: { ...row.data, parking: e.target.value } })} />
                      <Input placeholder="Map URL (Google Maps link)" value={row.data?.map_url || ""}
                        onChange={(e) => update(row.id, { data: { ...row.data, map_url: e.target.value } })}
                        className="md:col-span-2" />
                    </div>
                  )}

                  {section === "theme" && (
                    <div>
                      <Label>Sub-themes (one per line)</Label>
                      <Textarea
                        rows={3}
                        value={(row.data?.sub_themes || []).join("\n")}
                        onChange={(e) =>
                          update(row.id, {
                            data: { ...row.data, sub_themes: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) },
                          })
                        }
                      />
                    </div>
                  )}

                  {(section === "food" || section === "program") && (
                    <div>
                      <Label>{section === "food" ? "Menu items (one per line)" : "Schedule items (one per line, e.g. 9:00 AM — Worship)"}</Label>
                      <Textarea
                        rows={4}
                        value={(row.data?.items || []).join("\n")}
                        onChange={(e) =>
                          update(row.id, {
                            data: { ...row.data, items: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) },
                          })
                        }
                      />
                    </div>
                  )}

                  {(section === "speakers" || section === "flyer") && (
                    <Input
                      placeholder={section === "flyer" ? "Flyer image URL" : "Photo URL (optional)"}
                      value={row.data?.image_url || ""}
                      onChange={(e) => update(row.id, { data: { ...row.data, image_url: e.target.value } })}
                    />
                  )}

                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center gap-3">
                      <Label className="text-xs">Order</Label>
                      <Input
                        type="number"
                        className="w-20 h-8"
                        value={row.order_index}
                        onChange={(e) => update(row.id, { order_index: parseInt(e.target.value || "0", 10) })}
                      />
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={row.is_active}
                          onChange={(e) => update(row.id, { is_active: e.target.checked })}
                        /> Active
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => remove(row)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                      <Button size="sm" onClick={() => save(row)} disabled={savingId === row.id}>
                        {savingId === row.id ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-1" />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};
